import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useDisclosure } from '@nextui-org/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { pdf } from '@react-pdf/renderer';

import { GET_WORKOUTS, GET_ME, GET_WORKOUT_LOGS_STATS, GET_EXERCISE_FILTERS } from '../../graphql/queries';
import { trackEvent } from '../../utils/gt';
import { downloadBlob } from '../../utils/downloadBlob';
import { getNextWorkout } from '../../utils/workoutStats';
import { isProAccount, lockedWorkoutIds } from '../../utils/workoutLock';
import { trialContext } from '../../utils/subscription';

import workoutLimitVidEn from '../../assets/pro-only-vids/workout-limit-en.mp4';
import workoutLimitVidBr from '../../assets/pro-only-vids/workout-limit-br.mp4';
import exportPdfVidEn from '../../assets/pro-only-vids/export-pdf-en.mp4';
import exportPdfVidBr from '../../assets/pro-only-vids/export-pdf-br.mp4';

import { ModalUpgradePro } from '../ModalUpgradePro';
import { ModalTrialRestriction } from '../ModalTrialRestriction';
import { AllWorkoutsPDF } from '../WorkoutPDF';

export const PRO_WORKOUT_LIMIT = 10;
const FREE_WORKOUT_LIMIT = 1;

/**
 * The account's workouts as both pages show them (Meus Treinos and the editor, which keeps the list beside it on
 * desktop): in letter order, today's one marked, and the ones kept from an ended Pro subscription locked
 */
export const useWorkoutsData = () => {
  const { data: dataWorkouts, loading: isLoadingWorkouts, refetch: refetchWorkouts } = useQuery(GET_WORKOUTS);
  const { data: dataMe, refetch: refetchMe } = useQuery(GET_ME);
  const { data: dataLogs } = useQuery(GET_WORKOUT_LOGS_STATS);

  const workouts = dataWorkouts?.workouts?.data;
  const me = dataMe?.me;
  const isPro = isProAccount(me);
  const isTrialing = me?.plan === 'pro' && me?.subscriptionStatus === 'trialing';
  const limit = isPro ? PRO_WORKOUT_LIMIT : FREE_WORKOUT_LIMIT;
  // Kept from an ended Pro subscription: listed locked, never deleted
  const locked = useMemo(() => lockedWorkoutIds(workouts, me), [workouts, me]);
  const usable = useMemo(() => (workouts || []).filter((workout) => !locked.has(workout.id)), [workouts, locked]);
  const next = getNextWorkout(usable, dataLogs?.workoutLogs?.data || []);

  const rows = useMemo(
    () =>
      [...(workouts || [])]
        .sort((a, b) => (a.attributes?.name || '').localeCompare(b.attributes?.name || ''))
        .map((workout) => ({
          id: workout.id,
          name: workout.attributes.name,
          description: workout.attributes.description,
          motivation: workout.attributes.motivation,
          voiceInstructions: workout.attributes.voiceInstructions ?? true,
          image: workout.attributes.image,
          exercises: workout.attributes.exercises || [],
          isNext: workout.id === next?.id,
          isLocked: locked.has(workout.id),
        })),
    [workouts, locked, next?.id]
  );

  return {
    workouts,
    rows,
    me,
    isPro,
    isTrialing,
    limit,
    locked,
    usable,
    next,
    // Skeletons only until the first answer: a refetch keeps the list on screen
    isLoading: isLoadingWorkouts && !dataWorkouts,
    refetchWorkouts,
    refetchMe,
  };
};

/**
 * The exercise library, by exerciseId: what a saved exercise works and with what (the saved workout only keeps the
 * name and media). The same query the editor's search uses, so it's loaded once and shared from the cache.
 */
export const useExerciseLibrary = ({ skip = false } = {}) => {
  const { data, loading } = useQuery(GET_EXERCISE_FILTERS, { skip });
  const all = data?.exercises?.data;
  const byId = useMemo(() => new Map((all || []).map((exercise) => [exercise.attributes?.exerciseId, exercise.attributes])), [all]);
  return { exercises: all || [], byId, isLoading: loading && !data };
};

/**
 * The actions the list offers wherever it shows (new workout, export all, back to Pro) and the Pro offers they
 * open. `modals` goes into the page once.
 */
export const useWorkoutActions = ({ workouts, usable, me, isPro, isTrialing, limit, refetchMe }) => {
  const { t, i18n: { language } } = useTranslation();
  const navigate = useNavigate();
  const [isExportingAll, setExportingAll] = useState(false);
  const limitOffer = useDisclosure();
  const exportOffer = useDisclosure();
  const trialNotice = useDisclosure();

  // Export is Pro only and not part of the trial
  const canExport = () => {
    if (!isPro) {
      exportOffer.onOpen();
      return false;
    }
    if (isTrialing) {
      trialNotice.onOpen();
      return false;
    }
    return true;
  };

  // Resolves to false when the limit stopped it (the offer or the notice shows instead)
  const checkRoom = () => {
    if ((workouts?.length || 0) < limit) return true;
    // Pro already has the higher limit: tell them, don't pitch Pro again
    if (isPro) {
      toast.info(t('pages.Workouts.list.limitReached', { limit: PRO_WORKOUT_LIMIT }), { toastId: 'workout-limit' });
      return false;
    }
    limitOffer.onOpen();
    return false;
  };

  const handleNew = (state) => {
    if (checkRoom()) navigate('/workout', state ? { state } : undefined);
  };

  const handleExportAll = async () => {
    if (!canExport()) return;
    trackEvent('workouts_export_all_pdf', 'Workouts - Export All PDF', 'workouts', true);
    setExportingAll(true);
    try {
      const blob = await pdf(<AllWorkoutsPDF workouts={usable} language={language} />).toBlob();
      downloadBlob(blob, `${t('pages.Workouts.exportAllFilename')}.pdf`);
    } finally {
      setExportingAll(false);
    }
  };

  const handleUnlock = () => {
    trackEvent('workouts_kept_cta', 'Workouts - Kept Workouts CTA', 'workouts', true);
    navigate('/plans');
  };

  const modals = (
    <>
      <ModalUpgradePro
        isOpen={limitOffer.isOpen}
        onOpenChange={limitOffer.onOpenChange}
        onClose={limitOffer.onClose}
        videoSrc={language === 'pt' ? workoutLimitVidBr : workoutLimitVidEn}
      />
      <ModalUpgradePro
        isOpen={exportOffer.isOpen}
        onOpenChange={exportOffer.onOpenChange}
        onClose={exportOffer.onClose}
        videoSrc={language === 'pt' ? exportPdfVidBr : exportPdfVidEn}
        title={t('components.ModalUpgradePro.exportPdfTitle')}
        description={t('components.ModalUpgradePro.exportPdfDesc')}
      />
      <ModalTrialRestriction
        isOpen={trialNotice.isOpen}
        onOpenChange={trialNotice.onOpenChange}
        onClose={trialNotice.onClose}
        onSkipTrialSuccess={refetchMe}
        isCanceled={me?.cancelAtPeriodEnd}
      />
    </>
  );

  return {
    modals,
    canExport,
    checkRoom,
    handleNew,
    handleExportAll,
    isExportingAll,
    handleUnlock,
    unlockLabel: t('components.ModalUpgradePro.cta', { context: trialContext(me) }),
  };
};
